import type { QueryHashRef } from '../../hooks/gql';
import type { AssocMap } from '../fetch/types';
import { createPersistedQuery, resolvePersistedQuery } from './persisted-query';

interface ExecuteStringQuery<GqlVariables> {
  query: string;
  variables?: GqlVariables;
  assocMap?: AssocMap;
  useApq?: boolean;
}

interface ExecuteHashQuery<GqlVariables> {
  queryHashRef: QueryHashRef;
  variables?: GqlVariables;
  assocMap?: AssocMap;
}

export type ExecuteQueryArgs<GqlVariables> =
  | ExecuteStringQuery<GqlVariables>
  | ExecuteHashQuery<GqlVariables>;

const getGqlParamsFromStringQuery = async <GqlVariables>({
  query,
  variables,
  assocMap,
  useApq,
}: ExecuteStringQuery<GqlVariables>) => ({
  query,
  variables,
  assocMap,
  persistedQuery: useApq ? await createPersistedQuery(query) : null,
});

const getGqlParamsFromHashQuery = async <GqlVariables>({
  variables,
  assocMap,
  queryHashRef,
}: ExecuteHashQuery<GqlVariables>) => ({
  query: queryHashRef.current.query,
  variables,
  assocMap,
  persistedQuery: await resolvePersistedQuery(queryHashRef),
});

const hasStringQuery = <GqlVariables>(
  args: ExecuteQueryArgs<GqlVariables>
): args is ExecuteStringQuery<GqlVariables> => 'query' in args;

export const getGqlParams = <GqlVariables>(args: ExecuteQueryArgs<GqlVariables>) =>
  hasStringQuery(args) ? getGqlParamsFromStringQuery(args) : getGqlParamsFromHashQuery(args);
